import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  System,
  countHybrid,
  countStorage,
  countEnerclic,
  countEvcharge,
  countInverter,
  countMeter,
  PowersService,
  ProductModelService,
  getTelemetryStatus,
  AzzurroHubDataType,
} from 'ngx-core-services';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';

import {
  AppState,
  getSystemDefinitionDeviceIdsForTelemetries,
  requestPlantCardBlocksToShow,
  requestPlantCardStatus,
  SystemDefinition,
  updatePlantCardBlocksToShow,
  updatePlantCardStatus,
} from '..';
import { blockInfo } from '../../lib/plant-card-overview/plant-card-overview.model';
import { processAndGroupAlerts } from '../../lib/alerts/alert.utils';
import { AlertContent } from '../../lib/alerts/alert.model';

@Injectable()
export class PlantCardEffects {
  requestPlantCardBlocksToShow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestPlantCardBlocksToShow),
      withLatestFrom(this.store),
      map(([action, { systemState }]) => {
        const blocks = this.getBlocksToShow(systemState.systemList[action.systemId]);
        return updatePlantCardBlocksToShow({ systemId: action.systemId, blocks });
      }),
    )
  );

  requestPlantCardStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(requestPlantCardStatus),
      withLatestFrom(this.store),
      mergeMap(async ([action, { systemState, blockState }]) => {
        const isRTRuntimeActive = Boolean(blockState.blockList.RealTimeTelemetryComponent?.[action.systemId]?.active);
        if (isRTRuntimeActive) {
          // RT active — skip normal status fetch
          return updatePlantCardStatus({
            systemId: action.systemId,
            plantStatus: 'connectedAndUpdated',
            hasAlarms: false,
          });
        }
        const system = systemState.systemList[action.systemId];
        const forceRefresh = action.forceRefresh;
        return await this.getPlantStatus(system, forceRefresh);
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly powersService: PowersService,
    private readonly store: Store<AppState>,
  ) {}

  getBlocksToShow(system: System): blockInfo[] {
    const hasHybrid = countHybrid(system) > 0;
    const hasStorage = countStorage(system) > 0;
    const couldHaveBatteries = hasHybrid || hasStorage;

    const connextPresent = system.devices.some(device => {
      const product = new ProductModelService(device.id);
      return product.type?.name === 'connext';
    });

    const azzurroHubPresent = system.devices.some(device => {
      const product = new ProductModelService(device.id);
      return product.type?.name === 'azzurro-hub';
    });
    const enerclicCount = countEnerclic(system);
    const evchargeCount = countEvcharge(system);
    const inverterCount = countInverter(system);
    const meterCount = countMeter(system);

    const blocks: blockInfo[] = [];
    if (inverterCount > 0) {
      blocks.push({
        name: 'inverter',
        icon: 'customer_inverter',
        count: inverterCount,
      });
    }

    if( azzurroHubPresent && (system.aws?.ems_state_v2?.length) ) {
      const azzurroHubConfig = (system.aws?.ems_state_v2[0]) as AzzurroHubDataType;
      const evchargesCount = azzurroHubConfig.evCharger485?.length || 0 + azzurroHubConfig.evChargerCAN?.length || 0;
      if( evchargesCount > 0 ){
        blocks.push({
          name: 'evcharge',
          icon: 'customer_evcharge',
          count: evchargesCount,
        });
      }

      const heatpumpCount = azzurroHubConfig.heatPump.length || 0;
      if( heatpumpCount > 0 ){
        blocks.push({
          name: 'heatpump',
          icon: 'customer_heat_pump',
          count: heatpumpCount,
        });
      }
    }

    if (evchargeCount > 0) {
      blocks.push({
        name: 'evcharge',
        icon: 'customer_evcharge',
        count: evchargeCount,
      });
    }
    if (connextPresent) {
      blocks.push({
        name: 'connext',
        icon: 'customer_connext',
        count: 1,
      });
    }

    if (azzurroHubPresent) {
      blocks.push({
        name: 'azzurro-hub',
        icon: 'customer_azzurro_hub',
        count: 1,
      });
    }
    if (enerclicCount > 0) {
      blocks.push({
        name: 'combox',
        icon: 'customer_combox',
        count: 1,
      });
    }
    if (meterCount > 0) {
      blocks.push({
        name: 'meter',
        icon: 'customer_meter',
        count: 1,
      });
    }
    if (couldHaveBatteries) {
      blocks.push({
        name: 'battery',
        icon: 'customer_azzurro_battery',
        // @note: get battery 'count' from the telemetry in the future
      });
    }
    if (!couldHaveBatteries && inverterCount === 0 && evchargeCount === 0 && enerclicCount === 0 && meterCount === 0 && !connextPresent) {
      blocks.push({
        name: 'default_device',
        icon: 'customer_default_device',
      });
    }

    return blocks;
  }

  async getPlantStatus(system: SystemDefinition, forceRefresh: boolean) {
    const monitoringType = system?.monitoringType;
    const devices = getSystemDefinitionDeviceIdsForTelemetries(system, { onlyInstalled: true });

    const telemetry = await this.powersService.fetchPowersState(devices, monitoringType, forceRefresh);
    const sortedTelemetry = Object.keys(telemetry).reduce((acc: any, key) => {
      acc[key] = telemetry[key].sort((a, b) => (a.TS || 0) - (b.TS || 0)).pop();
      return acc;
    }, {});

    const telemetryForAlerts = Object.keys(sortedTelemetry).reduce((acc: any, key) => {
      const value = sortedTelemetry[key];
      if (value) {
        acc[key] = [value];
      }
      return acc;
    }, {} as Record<string, any[]>);

    const systemStatus = getTelemetryStatus(devices, sortedTelemetry);
    const plantStatus = monitoringType === 'Solarman'
      ? systemStatus?.checked === false
        ? 'notUpdated'
        : 'updated'
      : system?.data?.info?.connection.status === 'disconnected'
        ? 'disconnected'
        : systemStatus?.checked === false
          ? 'notUpdated'
          : 'connectedAndUpdated';

    const plantAlerts: AlertContent = processAndGroupAlerts(telemetryForAlerts).alerts;

    return updatePlantCardStatus({ systemId: system.uid!, plantStatus, hasAlarms: Object.keys(plantAlerts).length > 0 } );
  }
}
